/**
 * DemoDataInjector — populates the React Query cache with static demo data
 * when demo mode is active, and clears it when demo mode is deactivated.
 *
 * Two mechanisms work together:
 * 1. Upfront injection: populates known keys immediately on activation.
 * 2. Cache subscription: intercepts any NEW query added while demo is active
 *    and auto-populates it, regardless of what params the page passes.
 *
 * Mounted once near the top of the tree (inside QueryClientProvider).
 * No API calls, no database writes.
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { subscribeDemoMode, isDemoModeActive } from "@/lib/demoMode";
import {
  getGetTodayQueryKey,
  getListTasksQueryKey,
  getListProjectsQueryKey,
  getListInboxQueryKey,
  getListSuggestionsQueryKey,
  getGetMeQueryKey,
  getListMailAccountsQueryKey,
  getGetMailUnreadSummaryQueryKey,
  getGetMailPendingActionsSummaryQueryKey,
  getListDriftAlertsQueryKey,
  type ProjectSummary,
} from "@workspace/api-client-react";
import {
  getDemoToday,
  DEMO_TASKS,
  DEMO_PROJECTS,
  DEMO_INBOX,
  DEMO_SUGGESTIONS_DIGEST,
  DEMO_USER,
  DEMO_MAIL_ACCOUNTS,
  DEMO_MAIL_FOLDERS,
  DEMO_MAIL_MESSAGES,
  DEMO_MAIL_UNREAD,
  DEMO_MAIL_PENDING,
  DEMO_TASK_LABELS_BY_TASK_ID,
  DEMO_MAIL_ACCOUNT_ID,
  DEMO_CRM_CONTACTS,
  DEMO_CRM_FOLLOW_UPS,
  DEMO_DRIFT_ALERTS,
  DEMO_DECISION_LOG,
  DEMO_LIBRARY_STATUS,
  DEMO_LIBRARY_DOCUMENTS,
  DEMO_LIBRARY_ANALYSES,
  DEMO_LIBRARY_TASKS,
} from "@/lib/demo-data";

const DEMO_MAIL_FOLDERS_KEY  = `/api/mail/accounts/${DEMO_MAIL_ACCOUNT_ID}/folders`;
const DEMO_MAIL_MESSAGES_KEY = `/api/mail/accounts/${DEMO_MAIL_ACCOUNT_ID}/messages`;

/**
 * Given a query key, returns demo data if the key matches a known demo
 * endpoint, or `undefined` if this query should not be intercepted.
 */
function getDemoDataForKey(queryKey: readonly unknown[]): unknown | undefined {
  const prefix = queryKey[0];
  if (typeof prefix !== "string") return undefined;

  // Custom hand-rolled query: ["task-labels", "id1,id2,..."]
  if (prefix === "task-labels") {
    const idsStr = queryKey[1] as string | undefined;
    if (!idsStr) return { byTaskId: {} };
    const ids = idsStr.split(",").filter(Boolean);
    const byTaskId: Record<string, unknown> = {};
    for (const id of ids) {
      byTaskId[id] = DEMO_TASK_LABELS_BY_TASK_ID[id] ?? [];
    }
    return { byTaskId };
  }

  // Mail folders for the demo account
  if (prefix === DEMO_MAIL_FOLDERS_KEY) {
    return DEMO_MAIL_FOLDERS;
  }

  // Mail messages for the demo account (filter by folderId if provided)
  if (prefix === DEMO_MAIL_MESSAGES_KEY) {
    const params = queryKey[1] as Record<string, unknown> | undefined;
    if (params?.folderId) {
      return DEMO_MAIL_MESSAGES.filter((m) => m.folderId === params.folderId);
    }
    return DEMO_MAIL_MESSAGES;
  }

  switch (prefix) {
    case "/api/me":
      return DEMO_USER;
    case "/api/today":
      return getDemoToday();
    case "/api/tasks":
      return DEMO_TASKS;
    case "/api/inbox": {
      const params = queryKey[1] as Record<string, unknown> | undefined;
      if (params?.status && params.status !== "all") {
        return DEMO_INBOX.filter((item) => item.status === params.status);
      }
      return DEMO_INBOX;
    }
    case "/api/suggestions":
      return DEMO_SUGGESTIONS_DIGEST;
    case "/api/projects": {
      const params = queryKey[1] as Record<string, unknown> | undefined;
      if (params?.status) {
        return DEMO_PROJECTS.filter(
          (p: ProjectSummary) => p.status === params.status,
        );
      }
      return DEMO_PROJECTS;
    }
    case "/api/mail/accounts":
      return DEMO_MAIL_ACCOUNTS;
    case "/api/mail/unread-summary":
      return DEMO_MAIL_UNREAD;
    case "/api/mail/pending-actions/summary":
      return DEMO_MAIL_PENDING;
    case "crm-contacts": {
      const stageFilter = queryKey[1] as string | undefined;
      if (stageFilter && stageFilter !== "all") {
        return DEMO_CRM_CONTACTS.filter((c) => c.stage === stageFilter);
      }
      return DEMO_CRM_CONTACTS;
    }
    case "crm-follow-ups":
      return DEMO_CRM_FOLLOW_UPS;
    case "/api/drift-alerts":
      return DEMO_DRIFT_ALERTS;
    case "decision-log": {
      const projectFilter = queryKey[1] as string | undefined | null;
      if (projectFilter && projectFilter !== "all") {
        return DEMO_DECISION_LOG.filter((e) => e.projectId === projectFilter);
      }
      return DEMO_DECISION_LOG;
    }
    case "project-decision-log": {
      const projectId = queryKey[1] as string | undefined;
      if (projectId) {
        return DEMO_DECISION_LOG.filter((e) => e.projectId === projectId);
      }
      return DEMO_DECISION_LOG;
    }
    case "library-status":
      return DEMO_LIBRARY_STATUS;
    case "library-documents":
      return { documents: DEMO_LIBRARY_DOCUMENTS };
    case "library-analysis": {
      const docId = queryKey[1] as string | undefined;
      if (docId && DEMO_LIBRARY_ANALYSES[docId]) return DEMO_LIBRARY_ANALYSES[docId];
      return null;
    }
    case "library-tasks": {
      const docId = queryKey[1] as string | undefined;
      if (docId && DEMO_LIBRARY_TASKS[docId]) return DEMO_LIBRARY_TASKS[docId];
      return [];
    }
    case "library-chunks":
      return [];
    default:
      return undefined;
  }
}

function injectDemoData(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.setDefaultOptions({
    queries: { staleTime: Infinity, retry: false },
  });

  // Populate the most common key variants upfront so pages render immediately
  queryClient.setQueryData(getGetMeQueryKey(), DEMO_USER);
  queryClient.setQueryData(getGetTodayQueryKey(), getDemoToday());
  queryClient.setQueryData(getListTasksQueryKey(), DEMO_TASKS);
  queryClient.setQueryData(getListProjectsQueryKey(), DEMO_PROJECTS);
  queryClient.setQueryData(getListProjectsQueryKey({}), DEMO_PROJECTS);
  queryClient.setQueryData(
    getListProjectsQueryKey({ status: "active" }),
    DEMO_PROJECTS.filter((p) => p.status === "active"),
  );
  queryClient.setQueryData(getListInboxQueryKey(), DEMO_INBOX);
  queryClient.setQueryData(getListInboxQueryKey({}), DEMO_INBOX);
  queryClient.setQueryData(getListSuggestionsQueryKey(), DEMO_SUGGESTIONS_DIGEST);
  queryClient.setQueryData(
    getListSuggestionsQueryKey({ status: "pending" }),
    DEMO_SUGGESTIONS_DIGEST,
  );
  queryClient.setQueryData(getListMailAccountsQueryKey(), DEMO_MAIL_ACCOUNTS);
  queryClient.setQueryData(getGetMailUnreadSummaryQueryKey(), DEMO_MAIL_UNREAD);
  queryClient.setQueryData(
    getGetMailPendingActionsSummaryQueryKey(),
    DEMO_MAIL_PENDING,
  );
  // Mail folders + messages for the demo account
  queryClient.setQueryData([DEMO_MAIL_FOLDERS_KEY], DEMO_MAIL_FOLDERS);
  queryClient.setQueryData([DEMO_MAIL_MESSAGES_KEY], DEMO_MAIL_MESSAGES);
  queryClient.setQueryData(
    [DEMO_MAIL_MESSAGES_KEY, { folderId: DEMO_MAIL_FOLDERS[0]?.id, limit: 100 }],
    DEMO_MAIL_MESSAGES.filter((m) => m.folderId === DEMO_MAIL_FOLDERS[0]?.id),
  );
  // CRM
  queryClient.setQueryData(["crm-contacts"], DEMO_CRM_CONTACTS);
  queryClient.setQueryData(["crm-contacts", "all"], DEMO_CRM_CONTACTS);
  queryClient.setQueryData(["crm-follow-ups"], DEMO_CRM_FOLLOW_UPS);
  // Drift alerts
  queryClient.setQueryData(getListDriftAlertsQueryKey(), DEMO_DRIFT_ALERTS);
  // Decision log (global + per-project)
  queryClient.setQueryData(["decision-log", undefined], DEMO_DECISION_LOG);
  queryClient.setQueryData(["decision-log", null], DEMO_DECISION_LOG);
  queryClient.setQueryData(["decision-log", "all"], DEMO_DECISION_LOG);
  // Library
  queryClient.setQueryData(["library-status"], DEMO_LIBRARY_STATUS);
  queryClient.setQueryData(["library-documents"], { documents: DEMO_LIBRARY_DOCUMENTS });
  for (const [docId, analysis] of Object.entries(DEMO_LIBRARY_ANALYSES)) {
    queryClient.setQueryData(["library-analysis", docId], analysis);
  }
  for (const [docId, tasks] of Object.entries(DEMO_LIBRARY_TASKS)) {
    queryClient.setQueryData(["library-tasks", docId], tasks);
    queryClient.setQueryData(["library-chunks", docId], []);
  }
}

function clearDemoData(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.setDefaultOptions({
    queries: { staleTime: 0, retry: 3 },
  });
  void queryClient.resetQueries();
}

export default function DemoDataInjector() {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (isDemoModeActive()) {
      injectDemoData(queryClient);
    }

    const unsubscribeDemoMode = subscribeDemoMode(() => {
      if (isDemoModeActive()) {
        injectDemoData(queryClient);
      } else {
        clearDemoData(queryClient);
      }
    });

    // Intercept every new query added to the cache while demo is active.
    // This covers parameterised variants we didn't pre-populate upfront.
    const unsubscribeCache = queryClient.getQueryCache().subscribe((event) => {
      if (!isDemoModeActive()) return;
      if (event.type !== "added") return;

      const key = event.query.queryKey;
      const demoData = getDemoDataForKey(key);
      if (demoData !== undefined) {
        queryClient.setQueryData(key, demoData);
      }
    });

    return () => {
      unsubscribeDemoMode();
      unsubscribeCache();
    };
  }, [queryClient]);

  return null;
}
